import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '../context/SidebarContext';
import { supabase } from '../lib/supabase';
import {
  loadOnboardingSeen,
  isSeen as isOnboardingSeen,
  markSeen as markOnboardingSeen,
} from '../lib/onboardingSeen';
import { getProjectThumbnailSignedUrl } from '../utils/projectThumbnail';
import { resolveProjectBrandId } from '../utils/videoProject';
import { Plus, Trash2, Film, Loader2, MoreHorizontal, ChevronDown } from 'lucide-react';
import { WelcomeEditorModal } from '../components/WelcomeEditorModal';
import { useConfirmar } from './flujos/panels/Confirmar';
import { EncabezadoPantalla } from '../components/PantallaChrome';
import { ALTO_NAV_INFERIOR } from './v2/components/NavInferiorMobile';
import { DURACION_INICIAL_SEGUNDOS } from '../lib/videoProjectDefaults';
import {
  BLOQUE_ESQUELETO,
  EsqueletoSeccion,
  claseContenido,
} from '../components/EsqueletoSeccion';

interface VideoProject {
  id: string;
  name: string;
  duration: number;
  thumbnail_file_name: string | null;
  created_at: string;
  updated_at: string;
  brand_profile_id: string | null;
}

const formatRelativeDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'Ahora';
  if (diffMin < 60) return `Hace ${diffMin} min`;
  if (diffHours < 24) return `Hace ${diffHours}h`;
  if (diffDays === 1) return 'Ayer';
  if (diffDays < 7) return `Hace ${diffDays} días`;
  return date.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
};

const formatDuration = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

export const VideoProjects: React.FC = () => {
  const navigate = useNavigate();
  const { sidebarWidth } = useSidebar();

  const [projects, setProjects] = useState<VideoProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  // Bienvenida del editor (primera vez). El "ya la vi" se persiste en la DB
  // (profiles.onboarding_seen → llave "welcome"), cross-device, con fallback a
  // localStorage.
  const [showWelcome, setShowWelcome] = useState(false);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  // Sheet de acciones de un proyecto, en el teléfono. Estado APARTE de
  // `menuOpenId`: ese lo cierra un listener de click en `document` —así se
  // cierra el dropdown de escritorio al tocar afuera—, y con el sheet colgado
  // del mismo estado el cierre se comía la animación de salida. `…Animado`
  // maneja la entrada y la salida (montado sin animar → `translateY(100%)`).
  const [proyectoDelSheet, setProyectoDelSheet] = useState<string | null>(null);
  const [sheetAnimado, setSheetAnimado] = useState(false);
  const cerrarSheet = useCallback(() => {
    setSheetAnimado(false);
    window.setTimeout(() => setProyectoDelSheet(null), 250);
  }, []);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // Signed URL por proyecto (clave: project.id). El thumbnail vive en un bucket
  // privado, así que la columna `thumbnail_file_name` guarda el storage path.
  const [thumbUrls, setThumbUrls] = useState<Record<string, string>>({});
  // Brand filter dropdown (same style as the Historial filter). `null` selection
  // means "Todas las marcas". Defaults to all on every visit (not persisted) and
  // is only shown when the company has 2+ brands — with one brand there's
  // nothing to filter.
  const [brands, setBrands] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null);
  const [brandDropdownOpen, setBrandDropdownOpen] = useState(false);

  const fetchProjects = useCallback(async (cId: string) => {
    const { data, error } = await supabase
      .from('video_projects')
      .select('id, name, duration, thumbnail_file_name, created_at, updated_at, brand_profile_id')
      .eq('id_company', cId)
      .order('updated_at', { ascending: false });

    if (!error && data) {
      setProjects(data as VideoProject[]);
      const withThumb = (data as VideoProject[]).filter(p => p.thumbnail_file_name);
      Promise.all(
        withThumb.map(async p => {
          const url = await getProjectThumbnailSignedUrl(p.thumbnail_file_name as string);
          return [p.id, url] as const;
        }),
      ).then(pairs => {
        const next: Record<string, string> = {};
        for (const [id, url] of pairs) if (url) next[id] = url;
        setThumbUrls(next);
      });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user || cancelled) return;
        setUserId(user.id);
        const { data: profile } = await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', user.id)
          .single();
        const cId = (profile as any)?.id_company as string | undefined;
        if (!cId || cancelled) return;
        setCompanyId(cId);
        // Load the company's brands so the filter dropdown can list them. With a
        // single brand the dropdown stays hidden (nothing to filter).
        const { data: brandRows } = await supabase
          .from('brand_profiles')
          .select('id, name')
          .eq('id_company', cId)
          .eq('is_draft', false)
          .order('is_default', { ascending: false })
          .order('created_at', { ascending: true });
        if (!cancelled && brandRows) {
          setBrands(
            brandRows.map((b: any) => ({ id: b.id, name: b.name || 'Marca' })),
          );
        }
        await fetchProjects(cId);
      } catch {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fetchProjects]);

  // Mostrar la bienvenida la primera vez (una vez que tenemos companyId, así
  // "Crear proyecto" puede crear con la marca actual). Esperamos a que cargue el
  // estado de la DB (cross-device) antes de decidir, para NO mostrarla de nuevo
  // en otra PC si el usuario ya la vio.
  useEffect(() => {
    if (!companyId || !userId) return;
    let cancelled = false;
    (async () => {
      await loadOnboardingSeen(userId);
      if (cancelled) return;
      if (!isOnboardingSeen('welcome')) setShowWelcome(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, userId]);

  const markWelcomeSeen = () => {
    markOnboardingSeen('welcome', userId);
  };

  const handleCreate = async () => {
    if (!companyId || creating) return;
    setCreating(true);
    try {
      // Marca para el proyecto nuevo: el filtro de la lista → marca activa del
      // usuario → default de la empresa → crear una. Helper compartido con
      // "Editar con estudio" del historial (ver utils/videoProject.ts).
      const brandProfileId = await resolveProjectBrandId(companyId, {
        preferBrandId: selectedBrandId,
      });

      const { data, error } = await supabase
        .from('video_projects')
        .insert({
          id_company: companyId,
          name: 'Proyecto sin título',
          state: {},
          duration: DURACION_INICIAL_SEGUNDOS,
          brand_profile_id: brandProfileId,
        })
        .select('id')
        .single();
      if (!error && data) {
        navigate(`/editor-video/${data.id}`);
      }
    } catch {
      // ignore
    } finally {
      setCreating(false);
    }
  };

  // Borrar pregunta antes: era un clic, sin vuelta atrás (feedback de Nacho; ver
  // ADR-2026-11-03-borrar-un-proyecto-pregunta-antes). Y si la base no lo borra, el
  // proyecto queda en la lista en vez de desaparecer como si se hubiera borrado.
  const { confirmar, modal: modalConfirmar } = useConfirmar();
  const handleDelete = async (id: string) => {
    setMenuOpenId(null);
    const nombre = projects.find(p => p.id === id)?.name?.trim();
    const ok = await confirmar({
      titulo: nombre ? `¿Eliminar "${nombre}"?` : '¿Eliminar este proyecto?',
      texto: 'Se borra el proyecto con todo lo que tiene la timeline. No se puede deshacer.',
      aceptar: 'Eliminar',
      peligro: true,
    });
    if (!ok) return;
    setDeletingId(id);
    try {
      const { error } = await supabase.from('video_projects').delete().eq('id', id);
      if (!error) setProjects(prev => prev.filter(p => p.id !== id));
    } catch {
      // ignore: el proyecto sigue en la lista
    } finally {
      setDeletingId(null);
    }
  };

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpenId) return;
    const handler = () => setMenuOpenId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [menuOpenId]);

  // Close brand filter dropdown on outside click
  useEffect(() => {
    if (!brandDropdownOpen) return;
    const handler = () => setBrandDropdownOpen(false);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [brandDropdownOpen]);

  // `null` selection = all brands; otherwise narrow to the chosen brand.
  const selectedBrand = selectedBrandId
    ? brands.find(b => b.id === selectedBrandId) ?? null
    : null;
  const visibleProjects = selectedBrandId
    ? projects.filter(p => p.brand_profile_id === selectedBrandId)
    : projects;
  const showBrandFilter = !loading && projects.length > 0 && brands.length >= 2;

  return (
    <>
      {/* ── Teléfono ───────────────────────────────────────────────────────
          Antes acá había un cartel de "Solo disponible en desktop". El editor
          de un proyecto ya se abre en el teléfono
          (ADR-2026-09-16-el-estudio-de-video-en-el-telefono), así que la lista
          que lleva a él no podía seguir siendo una puerta cerrada.

          El reparto sale de cómo lo resuelven las apps de edición de video en
          iOS (referencias de Mobbin: CapCut, Riveo, GoPro Quik, los borradores
          de Reels de Instagram, Edits, Canva):

          · **Un botón grande y siempre a la vista** para el proyecto nuevo —
            en CapCut es una tarjeta ancha arriba, en GoPro Quik y en Edits una
            pastilla flotante abajo a la derecha; lo que comparten es que
            "empezar algo nuevo" no se esconde en un ícono de la barra. Acá es
            flotante (ver el botón, más abajo en este archivo).
          · **Los proyectos como FILAS**, no como grilla de dos columnas: en una
            columna de 390px, una miniatura 16:9 partida al medio deja el nombre
            en dos renglones y la fecha cortada. La fila —miniatura chica a la
            izquierda, nombre y "editado hace…" a la derecha— es lo que usan
            CapCut y los borradores de Reels, y es lo que sirve para lo que se
            viene a hacer acá: reconocer un proyecto y seguirlo.
          · **El menú de cada fila, en un sheet de abajo** (Google Drive,
            Obsidian, monday): el dropdown de 40px de escritorio vive de `hover`
            y de un click preciso, dos cosas que en un teléfono no existen.
          · **El filtro de marcas, como pastillas** en vez del desplegable:
            se ve qué marcas hay sin abrir nada, y cada una es un blanco de 32px.

          El título va escrito acá y no con `MobilePageTitle` a propósito: ese
          componente es `sm:hidden` y esta pantalla corta en `md` —el layout de
          escritorio es `hidden md:block`—, así que entre 640 y 767px la pantalla
          se habría quedado sin nombre. La tipografía es la misma. */}
      <div className="md:hidden px-4 pb-32">
        <h1 className="pl-2 mb-4 text-[26px] leading-tight font-semibold tracking-[-0.01em] text-brand-black">
          Proyectos de video
        </h1>

        {/* Sin filtro de marca: en escritorio hay un desplegable en la fila
            del título, acá había una tira de pastillas y se sacó a pedido del
            usuario. La lista del teléfono muestra los proyectos de la empresa
            entera, que es lo que hace el desplegable en "Todas las marcas".
            `selectedBrandId` sigue vivo —lo usa el desplegable de escritorio, y
            lo lee `handleCreate` para elegir la marca del proyecto nuevo. */}

        {/* Cargando: las mismas filas, en gris. Mismo criterio que el resto de
            las secciones (ADR-2026-08-03-todas-las-secciones-cargan-igual): lo
            que entra después tiene la forma de lo que se ve mientras carga. */}
        {loading && (
          <div className="mt-5 divide-y divide-black/[0.06]">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 py-3">
                <div className="w-[104px] aspect-video rounded-[8px] bg-black/[0.06] animate-pulse shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className={`h-[19px] w-[60%] ${BLOQUE_ESQUELETO}`} />
                  <div className={`mt-1 h-[17px] w-[40%] ${BLOQUE_ESQUELETO}`} />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Sin nada: sin botón propio. El de crear es el flotante de abajo a la
            derecha, que también está a la vista con la lista vacía. */}
        {!loading && projects.length === 0 && (
          <div className="flex flex-col items-center text-center px-6 pt-16">
            <div className="w-14 h-14 rounded-[13px] bg-black/[0.04] flex items-center justify-center mb-4">
              <Film className="w-6 h-6 text-neutral-400" />
            </div>
            <h3 className="text-[17px] font-semibold text-black/90 mb-1">Sin proyectos</h3>
            <p className="text-[15px] text-black/45 max-w-[260px]">
              Creá tu primer proyecto para empezar a editar con IA.
            </p>
          </div>
        )}

        {/* Las filas. Toda la fila es el blanco para abrir el proyecto; los
            tres puntos son un botón aparte (44px de alto real) que corta la
            propagación para no abrir el editor al pedir el menú.

            La fila no puede ser un `<button>` de verdad porque adentro tiene
            otro (los tres puntos) y un botón no anida. Como se anuncia con
            `role="button"` y recibe foco, tiene que abrirse con Enter y con
            Espacio igual que uno nativo: sin esto quien navega con teclado
            llegaba a la fila y no podía entrar al proyecto. El `target ===
            currentTarget` deja pasar las teclas que van a los tres puntos, que
            se activan solos. */}
        {!loading && projects.length > 0 && (
          <div className="mt-5 divide-y divide-black/[0.06]">
            {projects.map(project => (
              <div
                key={project.id}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/editor-video/${project.id}`)}
                onKeyDown={e => {
                  if (e.target !== e.currentTarget) return;
                  if (e.key !== 'Enter' && e.key !== ' ') return;
                  // Espacio scrollea la página si no se lo frena.
                  e.preventDefault();
                  navigate(`/editor-video/${project.id}`);
                }}
                className="flex items-center gap-3 py-3 -mx-2 px-2 rounded-[10px] active:bg-black/[0.04] transition-colors"
              >
                <div className="relative w-[104px] aspect-video rounded-[8px] overflow-hidden bg-black/[0.04] flex items-center justify-center shrink-0">
                  {project.thumbnail_file_name && thumbUrls[project.id] ? (
                    <img
                      src={thumbUrls[project.id]}
                      alt={project.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Film className="w-5 h-5 text-neutral-300" />
                  )}
                  {/* La duración va sobre la miniatura, como en cualquier
                      reproductor: en la fila de texto competía con la fecha,
                      que es el dato con el que se reconoce el proyecto. */}
                  <span className="absolute bottom-1 right-1 bg-black/60 text-white text-[10px] font-medium px-1.5 py-[1px] rounded">
                    {formatDuration(project.duration)}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[15px] font-medium text-black/90 truncate">
                    {project.name}
                  </p>
                  <p className="text-[13px] text-black/45 mt-0.5">
                    Editado {formatRelativeDate(project.updated_at)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    setProyectoDelSheet(project.id);
                    requestAnimationFrame(() => setSheetAnimado(true));
                  }}
                  aria-label={`Opciones de ${project.name}`}
                  className="w-11 h-11 -mr-2 shrink-0 inline-flex items-center justify-center rounded-full text-black/40 active:bg-black/[0.06] transition-colors"
                >
                  <MoreHorizontal className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Nuevo proyecto, flotante (teléfono) ────────────────────────────
          Abajo a la derecha, por encima de la barra de navegación: es el
          pulgar. Va como pastilla con etiqueta y no como círculo con un "+"
          pelado —es un FAB extendido, el de "Create an Edit" de GoPro Quik—
          porque en una pantalla que puede estar vacía el ícono solo no dice
          qué crea.

          El `bottom` sale de `ALTO_NAV_INFERIOR`, la misma constante con la que
          se dibuja la barra: si la barra cambia de alto, el botón la sigue en
          vez de quedar montado encima.

          Los 40px de aire no son decorativos: el botón de **Generar** de la
          barra es un círculo de 56px anclado en `bottom-[30px]`, así que
          SOBRESALE 22px por encima del borde de la barra. Con 16px la pastilla
          se le montaba justo ahí —además de quedar pegada al borde—; con 40
          pasa 18px por arriba del círculo.

          `z-40` es el de la barra: el botón está ARRIBA de ella, no encima, y
          el velo del sheet (z-200) lo tapa cuando el sheet se abre, que es lo
          que corresponde. */}
      <button
        type="button"
        onClick={handleCreate}
        disabled={creating}
        aria-label="Nuevo proyecto"
        className="md:hidden fixed right-4 z-40 h-12 pl-4 pr-5 inline-flex items-center gap-2 rounded-full bg-brand-primary text-white text-[15px] font-semibold shadow-[0_6px_20px_rgb(var(--brand-primary-rgb)_/_0.35)] active:opacity-90 transition-opacity disabled:opacity-60"
        style={{ bottom: `calc(${ALTO_NAV_INFERIOR} + 40px)` }}
      >
        {creating ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Plus className="w-4 h-4" />
        )}
        Nuevo proyecto
      </button>

      {/* ── Sheet de acciones de un proyecto (teléfono) ─────────────────────
          Alto automático, no `100dvh`: son dos filas. El reparto —bloque
          blanco con las acciones, "Cancelar" en su propio bloque, destructiva
          en rojo— es el de los sheets de archivo de iOS (Google Drive,
          Obsidian, monday.com en Mobbin) y el mismo lenguaje que el resto de
          los sheets de la app (velo negro al 40%, fondo `#F2F2F7`, manija).

          No reusa `OptionSheet` porque ese es de alto completo con header de
          Cancelar/Aceptar: acá no hay nada que "aceptar", cada fila ES la
          acción. Y `stopPropagation`: la lista cierra su menú de escritorio con
          un listener de click en `document`, y sin cortar acá el primer toque
          adentro del sheet lo cerraba. */}
      {proyectoDelSheet && (
        <>
          <div
            className={`md:hidden fixed inset-0 z-[200] bg-black/40 transition-opacity duration-250 ${sheetAnimado ? 'opacity-100' : 'opacity-0'}`}
            onClick={cerrarSheet}
          />
          <div
            className="md:hidden fixed inset-x-0 bottom-0 z-[201] bg-[#F2F2F7] rounded-t-[13px]"
            style={{
              paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
              transform: sheetAnimado ? 'translateY(0)' : 'translateY(100%)',
              transition: 'transform 0.25s ease-out',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div className="py-2">
              <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
            </div>
            {/* Qué proyecto es: el sheet se abre desde una fila y sin el nombre
                no hay forma de confirmar que es el que se tocó. */}
            <p className="px-5 pb-2 text-[13px] text-black/45 truncate">
              {projects.find(p => p.id === proyectoDelSheet)?.name}
            </p>
            <div className="mx-3 rounded-[13px] bg-white overflow-hidden">
              <button
                type="button"
                onClick={() => {
                  void handleDelete(proyectoDelSheet);
                  cerrarSheet();
                }}
                disabled={deletingId === proyectoDelSheet}
                className="w-full h-[52px] px-4 inline-flex items-center gap-3 text-[17px] text-red-600 active:bg-black/[0.04] transition-colors disabled:opacity-60"
              >
                {deletingId === proyectoDelSheet ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Trash2 className="w-5 h-5" />
                )}
                Eliminar proyecto
              </button>
            </div>
            <button
              type="button"
              onClick={cerrarSheet}
              className="mt-2 mx-3 w-[calc(100%-1.5rem)] h-[52px] rounded-[13px] bg-white text-[17px] font-semibold text-black/90 active:bg-black/[0.04] transition-colors"
            >
              Cancelar
            </button>
          </div>
        </>
      )}

      {/* Desktop */}
      {/* De `md` a `lg` sigue siendo lo que era: un panel FIJO a pantalla
          completa, anclado al ancho del sidebar, con su propio scroll y su
          propio lienzo (`surface-bg`). De `lg` para arriba esta ruta se monta
          SIEMPRE dentro del shell nuevo (`ShellApp` elige por viewport con el
          mismo breakpoint), y ahí ese tratamiento rompía tres cosas: tapaba la
          barra de créditos del shell (`inset-0` arranca en y=0), pintaba un
          lienzo distinto al del resto de las secciones, y se llevaba el scroll
          afuera del scroller del shell. Con `lg:` vuelve al flujo normal y queda
          como una sección más — ADR-2026-08-03-titulos-secciones-igual-que-inicio.
          `left` en el `style` deja de tener efecto solo, porque no aplica a un
          elemento estático. */}
      <div
        className="hidden md:block fixed inset-0 z-20 bg-surface-bg overflow-y-auto lg:static lg:z-auto lg:bg-transparent lg:overflow-visible"
        style={{ left: sidebarWidth }}
      >
        {modalConfirmar}
        {/* Bienvenida del editor (primera vez) → "Crear proyecto" crea con la
            marca actual y entra al editor (donde arranca el tour guiado). */}
        <WelcomeEditorModal
          open={showWelcome}
          creating={creating}
          onCreate={() => {
            markWelcomeSeen();
            void handleCreate();
          }}
          onSkip={() => {
            markWelcomeSeen();
            setShowWelcome(false);
          }}
        />
        {/* La columna de 896px y sus paddings son del panel fijo. Dentro del
            shell la columna la define el propio shell (`max-w-[90%]`) y el aire
            de arriba del título lo pone su regla compartida (40px): si estos se
            mantuvieran, el título quedaría 24px más a la derecha y 40px más
            abajo que el de `/inicio`. */}
        {/* Mientras carga, la pantalla entera va en esqueleto y después entra de una
            vez, como el resto de las secciones
            (ADR-2026-08-03-todas-las-secciones-cargan-igual). Antes el título, el
            breadcrumb y los filtros se dibujaban enseguida y el cuerpo era un spinner:
            el usuario veía media pantalla y esperaba la otra mitad.
            El breadcrumb va en `antesDeLaFila` y no `antesDelTitulo`: en esta pantalla
            es un elemento suelto ARRIBA de la fila del título, no adentro como en
            `/plantillas`, y de eso depende de qué lado le caen los 40px del shell. */}
        {loading && (
          <EsqueletoSeccion
            className="max-w-4xl mx-auto px-6 py-10 lg:max-w-none lg:px-0 lg:py-0"
            claseFilaTitulo="flex items-center justify-between mb-8"
            anchoTitulo="w-[280px]"
            antesDeLaFila={
              <span
                className={`hidden lg:inline-flex mb-4 w-[190px] text-[13px] ${BLOQUE_ESQUELETO}`}
              >
                &nbsp;
              </span>
            }
          >
            {/* La grilla real: 2 columnas, 3 en `lg`. Cada tarjeta es la miniatura en
                16:9 más una franja de datos de 45px (`px-3 py-2.5` con dos renglones
                de 13 y 11px). */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-lg border border-black/[0.06] overflow-hidden"
                >
                  <div className="aspect-video bg-black/[0.06] animate-pulse" />
                  <div className="px-3 py-2.5">
                    {/* 20 y 17px: los altos de línea reales de los dos renglones
                        (13px y 11px). Medido — con 17 y 14 la tarjeta quedaba 3px
                        más baja que la de verdad. */}
                    <div className={`h-[20px] w-[70%] ${BLOQUE_ESQUELETO}`} />
                    <div className={`mt-0.5 h-[17px] w-[45%] ${BLOQUE_ESQUELETO}`} />
                  </div>
                </div>
              ))}
            </div>
          </EsqueletoSeccion>
        )}
        <div
          className={`max-w-4xl mx-auto px-6 py-10 lg:max-w-none lg:px-0 lg:py-0 ${claseContenido(!loading)}`}
        >
          {/* Breadcrumb — sólo dentro del shell nuevo, que es donde hay un "Inicio"
              al que volver (ADR-2026-08-03-breadcrumbs-asistente-plantillas-estudio).
              Esta pantalla no está en el sidebar, así que era la única sección sin
              ninguna forma de volver a la home que no fuera el botón del navegador.

              Va ARRIBA del título, así que se lo lleva los 40px de aire de la regla
              del shell y el título queda más abajo que el de `/inicio`. Es el mismo
              intercambio que en `/plantillas`. */}
          <EncabezadoPantalla
            variante="seccion"
            migas={[
              { label: 'Inicio', onClick: () => navigate('/interfaz-v2') },
              { label: 'Estudio de video' },
            ]}
          />
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            {/* Sin bajada: el título va solo, como en el resto de las secciones del
                shell. Decía "Creá y editá videos con IA" y no agregaba nada que el
                título no dijera. */}
            <div>
              <h1 className="text-[17px] font-semibold text-brand-black/90">Proyectos de video</h1>
            </div>
            <div className="flex items-center gap-2">
              {showBrandFilter && (
                <div className="relative">
                  <button
                    type="button"
                    onClick={e => {
                      e.stopPropagation();
                      setBrandDropdownOpen(o => !o);
                    }}
                    className={`py-2 px-4 inline-flex items-center justify-between gap-2 border rounded-md text-[13px] font-medium shadow-sm outline-none transition-colors cursor-pointer whitespace-nowrap bg-surface-card hover:bg-surface-elevated text-neutral-900 border-neutral-200`}
                  >
                    <span className="max-w-[160px] truncate">
                      {selectedBrand?.name ?? 'Todas las marcas'}
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 text-neutral-400 transition-transform ${brandDropdownOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {brandDropdownOpen && (
                    <div className="absolute z-50 mt-1 right-0 min-w-[180px] bg-surface-card border border-neutral-200 rounded-md shadow-md py-1 animate-fade-in">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedBrandId(null);
                          setBrandDropdownOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-[13px] transition-colors ${
                          selectedBrandId === null
                            ? 'bg-neutral-100 text-neutral-900 font-medium'
                            : 'text-neutral-600 hover:bg-neutral-100'
                        }`}
                      >
                        Todas las marcas
                      </button>
                      {brands.map(b => (
                        <button
                          key={b.id}
                          type="button"
                          onClick={() => {
                            setSelectedBrandId(b.id);
                            setBrandDropdownOpen(false);
                          }}
                          className={`w-full text-left px-3 py-2 text-[13px] truncate transition-colors ${
                            selectedBrandId === b.id
                              ? 'bg-neutral-100 text-neutral-900 font-medium'
                              : 'text-neutral-600 hover:bg-neutral-100'
                          }`}
                        >
                          {b.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {!loading && projects.length > 0 && (
                <button
                  onClick={handleCreate}
                  disabled={creating}
                  className="py-2 px-4 rounded-md text-[13px] font-medium shadow-sm transition-colors flex items-center gap-1.5 bg-brand-primary hover:bg-brand-primary-hover text-white disabled:opacity-60"
                >
                  {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                  Nuevo proyecto
                </button>
              )}
            </div>
          </div>

          {/* Acá iba un spinner centrado mientras cargaba. Se fue con el esqueleto de
              arriba: eran las dos mitades del mismo estado
              (ADR-2026-08-03-todas-las-secciones-cargan-igual). */}

          {/* Empty state */}
          {!loading && projects.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-14 h-14 rounded-lg bg-neutral-100 flex items-center justify-center mb-4">
                <Film className="w-6 h-6 text-neutral-400" />
              </div>
              <h3 className="text-[15px] font-semibold text-brand-black/90 mb-1">Sin proyectos</h3>
              <p className="text-[13px] text-[#3C3C43]/60 max-w-xs mb-5">
                Creá tu primer proyecto de video para empezar a editar con IA.
              </p>
              <button
                onClick={handleCreate}
                disabled={creating}
                className="py-2.5 px-5 rounded-md text-[13px] font-medium shadow-sm transition-colors flex items-center gap-1.5 bg-brand-primary hover:bg-brand-primary-hover text-white disabled:opacity-60"
              >
                {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                Crear proyecto
              </button>
            </div>
          )}

          {/* Filtered-empty: projects exist, but none for the active brand */}
          {!loading && projects.length > 0 && visibleProjects.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-14 h-14 rounded-lg bg-neutral-100 flex items-center justify-center mb-4">
                <Film className="w-6 h-6 text-neutral-400" />
              </div>
              <h3 className="text-[15px] font-semibold text-brand-black/90 mb-1">
                Sin proyectos en {selectedBrand?.name}
              </h3>
              <p className="text-[13px] text-[#3C3C43]/60 max-w-xs mb-5">
                No tenés proyectos de video en esta marca todavía. Creá uno nuevo o cambiá el filtro a "Todas las marcas".
              </p>
              <button
                onClick={handleCreate}
                disabled={creating}
                className="py-2.5 px-5 rounded-md text-[13px] font-medium shadow-sm transition-colors flex items-center gap-1.5 bg-brand-primary hover:bg-brand-primary-hover text-white disabled:opacity-60"
              >
                {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                Crear proyecto
              </button>
            </div>
          )}

          {/* Project grid */}
          {!loading && visibleProjects.length > 0 && (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Existing projects */}
              {visibleProjects.map(project => (
                <div
                  key={project.id}
                  className="group relative rounded-lg bg-surface-card border border-black/[0.06] hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/editor-video/${project.id}`)}
                >
                  {/* Thumbnail */}
                  <div className="aspect-video bg-brand-black/[0.03] flex items-center justify-center relative rounded-t-lg overflow-hidden">
                    {project.thumbnail_file_name && thumbUrls[project.id] ? (
                      <img
                        src={thumbUrls[project.id]}
                        alt={project.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Film className="w-8 h-8 text-neutral-300" />
                    )}
                    {/* Duration badge */}
                    <div className="absolute bottom-2 right-2 bg-black/60 text-white text-[11px] font-medium px-1.5 py-0.5 rounded">
                      {formatDuration(project.duration)}
                    </div>
                  </div>

                  {/* Info */}
                  <div className="px-3 py-2.5 flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] font-medium text-brand-black/90 truncate">{project.name}</p>
                      <p className="text-[11px] text-[#3C3C43]/60 mt-0.5">
                        Editado {formatRelativeDate(project.updated_at)}
                      </p>
                    </div>

                    {/* Menu button */}
                    <div className="relative">
                      <button
                        onClick={e => { e.stopPropagation(); setMenuOpenId(menuOpenId === project.id ? null : project.id); }}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600 transition-colors opacity-0 group-hover:opacity-100"
                      >
                        <MoreHorizontal className="w-4 h-4" />
                      </button>

                      {/* Dropdown */}
                      {menuOpenId === project.id && (
                        <div
                          className="absolute right-0 top-8 z-50 w-40 bg-surface-card rounded-md shadow-lg border border-black/[0.06] py-1 animate-in fade-in slide-in-from-top-1 duration-150"
                          onClick={e => e.stopPropagation()}
                        >
                          <button
                            onClick={() => handleDelete(project.id)}
                            disabled={deletingId === project.id}
                            className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-red-600 hover:bg-red-50 transition-colors disabled:opacity-60"
                          >
                            {deletingId === project.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="w-3.5 h-3.5" />
                            )}
                            Eliminar
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default VideoProjects;
